import React, { useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  StatusBar,
  Platform,
  Image,
  TouchableOpacity,
  Alert,
  ScrollView,
  Modal,
  Pressable,
  Animated,
  ActivityIndicator,
} from 'react-native';
import {
  v2GetAccountOutflowOptions,
  v2CreateAccountOutflow,
  v2GetMyBalances,
} from '../../../services/v2/api';
import type {
  V2Balance,
  V2AccountActionOptions,
  V2CreateAccountOutflowRequest,
} from '../../../types/v2';
import TabBar from '../../../components/TabBar';
import AmountCaret from '../../../components/UIComponents/AmountCaret';
import SimpleHeader from '../../../components/SimpleHeader';
import TokenIcon from '../../../components/UIComponents/TokenIcon';
import { COLORS } from '../../../theme/themeTokens';
import { COLORS_WRONG } from '../../../theme/colorsWrong';
import { formatAmount, parseAmount, numberToAmountDisplay, formatTokenAmount } from '../../../utils/amountFormat';
import { MAX_AMOUNT_DECIMALS, toRequestAmount } from '../../../utils/moneyAmount';
import NumericKeypad from '../../../components/NumericKeypad';
import PINConfirmModal from '../../../components/UIComponents/PINConfirmModal';
import { usePIN } from '../../../contexts/PINContext';
import { formatDepositMeta } from '../../../utils/depositMeta';
import { useSwipeDownDismiss } from '../../../hooks/useSwipeDownDismiss';
import { WEB_SHEET, WEB_SHEET_BACKDROP, WEB_SHEET_MODAL_ANIMATION } from '../../../../web/sheet';
import { useIdempotencyKey } from '../../../hooks/useIdempotencyKey';

// Top inset for the destination-picker bottom-sheet. Pinned to the
// "To current account" tile top — same arithmetic as TopUp's picker.
//   status bar (20 iOS / 28 Android) + SimpleHeader (~48)
//   Matches the TopUpDepositScreen offset (+109) so both screens'
//   pickers open at the same vertical position regardless of how many
//   tiles sit above them on the form.
const PICKER_TOP_INSET = (Platform.OS === 'android' ? 28 : 20) + 109;

// formatBalanceLikeCard — теперь delegate к formatTokenAmount
// чтобы UI-балансы шли через ту же string-truncation что и Maximum-кнопка.
// Раньше функция использовала toFixed (округление вверх) — это давало
// рассинхрон: balance label показывал "3,06839508" а Maximum давал
// "3,06839507" для того же source-значения. Теперь оба пути truncate.
// Потолок знаков на экране живёт в formatTokenAmount: token_decimals — это
// точность контракта (18 у USDT), а не то, сколько цифр читает человек.
const formatBalanceLikeCard = (amount: number | string, decimals = 2): string =>
  formatTokenAmount(amount, decimals);

const formatTermDate = (iso?: string | null): string => {
  if (!iso) {
    return '';
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return '';
  }
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(-2);
  return `${dd}.${mm}.${yy}`;
};

// 30-дневное окно снятия. Партный расчёт с backend'ным
// computeWithdrawWindow: доступно ТОЛЬКО первые 30 дней от
// deposit_date_from. Для депозитов без даты (legacy) — окно закрыто.
// Используется в picker'е "From deposit account" чтобы disable'ить
// deposit'ы у которых окно уже закрылось.
const WITHDRAW_WINDOW_DAYS = 30;
const isWithdrawWindowOpen = (openDateISO: string | null | undefined): boolean => {
  if (!openDateISO) return false;
  const openDate = new Date(openDateISO);
  if (Number.isNaN(openDate.getTime())) return false;
  const until = openDate.getTime() + WITHDRAW_WINDOW_DAYS * 24 * 60 * 60 * 1000;
  return Date.now() < until;
};

interface DestinationOption {
  key: string;
  symbol: string;
  networkSymbol: string;
  networkName: string;
  iconUrl: string | null;
  amount: number;
  balance: V2Balance;
}

interface OutflowDepositScreenProps {
  accountId: string;
  onBack: () => void;
  onNavigate: (screen: string) => void;
  onOutflowSuccess?: () => void;
  /** Triggered when the user taps the "close the deposit" link in the
   *  Partial-withdraw warning bar. Parent navigates to CloseDepositScreen
   *  for the same accountId. */
  onCloseDeposit?: () => void;
  repeatPrefill?: { destinationBalanceId?: string; amount?: string };
}

const OutflowDepositScreen: React.FC<OutflowDepositScreenProps> = ({
  accountId,
  onBack,
  onNavigate,
  onOutflowSuccess,
  onCloseDeposit,
  repeatPrefill,
}) => {
  const [options, setOptions] = useState<V2AccountActionOptions | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedDestination, setSelectedDestination] = useState<V2Balance | null>(null);
  const [pickerVisible, setPickerVisible] = useState(false);
  // Swipe-to-dismiss for the destination ("To current") picker.
  const destSwipe = useSwipeDownDismiss(
    pickerVisible,
    () => setPickerVisible(false),
  );
  const [amountStr, setAmountStr] = useState('');
  const [keypadVisible, setKeypadVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pinModalVisible, setPinModalVisible] = useState(false);
  // Success modal — показывается после успешного POST /outflow, заменяет
  // немедленный onOutflowSuccess+onBack. Mirror'ит OpenDepositScreen
  // success modal pattern. Закрытие → onOutflowSuccess + onBack.
  const [successModalVisible, setSuccessModalVisible] = useState(false);
  // Active source investment-account — default = prop accountId; the
  // "From deposit account" picker swaps this when user owns 2+ deposits.
  const [activeAccountId, setActiveAccountId] = useState<string>(accountId);
  // User's investment balances feeding the source picker. Fetched once
  // on mount.
  const [investmentBalances, setInvestmentBalances] = useState<V2Balance[]>([]);
  const [depositPickerVisible, setDepositPickerVisible] = useState(false);
  // Swipe-to-dismiss for the source ("From deposit") picker.
  const depositSwipe = useSwipeDownDismiss(
    depositPickerVisible,
    () => setDepositPickerVisible(false),
  );
  const { isPINSetup, isPINSkipped } = usePIN();
  // One attempt per withdrawal: a press after a lost answer resends it with
  // the same key, and the server does not file it twice.
  const idempotency = useIdempotencyKey<{ accountId: string; body: V2CreateAccountOutflowRequest }>();

  const openDepositPicker = () => {
    // Skip the picker when there's only one deposit — chevron hidden,
    // tile disabled in that case.
    if (investmentBalances.length < 2) {
      return;
    }
    setDepositPickerVisible(true);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await v2GetMyBalances();
        if (cancelled) {
          return;
        }
        // Filter to classic deposit accounts. Note: после rename'а
        // (migrations 00096/00098) account_type_code='deposit' = классический
        // срочный депозит, а 'investment' = T-Fund-like (бывший t_fund) —
        // не для этого screen'а.
        //
        // Дополнительно скрываем deposit'ы с available_amount=0 — это
        // означает pending close-заявку: CreateAccountClose локирует весь
        // investment balance, so available падает до 0. Withdraw уходящего
        // в закрытие депозита не имеет смысла — юзеру не показываем.
        //
        // Также скрываем deposit'ы у которых 30-дневное окно снятия уже
        // закрылось — они не могут быть источником withdraw'а, backend
        // всё равно вернёт 403 `withdraw_window_expired`.
        const invest = (res?.balances ?? []).filter((b: V2Balance) => {
          if (b.account_type_code !== 'deposit') return false;
          const available = parseFloat(b.available_amount ?? b.amount ?? '0');
          if (available <= 0) return false;
          return isWithdrawWindowOpen(b.deposit_date_from);
        });
        setInvestmentBalances(invest);
      } catch (err) {
        console.warn('[OutflowDeposit] Failed to load investment balances:', err);
        if (!cancelled) {
          setInvestmentBalances([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await v2GetAccountOutflowOptions(activeAccountId);
        if (cancelled) {
          return;
        }
        setOptions(res);
        const dests = res.balances ?? [];
        const repeatedDestination = dests.find((balance) => balance.id === repeatPrefill?.destinationBalanceId);
        if (repeatedDestination) {
          setSelectedDestination(repeatedDestination);
        } else if (dests.length === 1) {
          setSelectedDestination(dests[0]);
        } else if (dests.length > 1) {
          // Pre-select by largest available — same as TopUp / Close.
          const sorted = [...dests].sort(
            (a, b) =>
              parseFloat(b.available_amount ?? b.amount ?? '0') -
              parseFloat(a.available_amount ?? a.amount ?? '0'),
          );
          setSelectedDestination(sorted[0]);
        }
        if (repeatPrefill?.amount) {
          // The token's places, not two: a repeated 0,0006 BTC must not read "0,00".
          setAmountStr(numberToAmountDisplay(
            repeatPrefill.amount,
            Math.min(res.balance?.token_decimals ?? 2, MAX_AMOUNT_DECIMALS),
          ));
        }
      } catch (err) {
        console.warn('[OutflowDeposit] Failed to load options:', err);
        setOptions(null);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeAccountId, repeatPrefill?.amount, repeatPrefill?.destinationBalanceId]);

  const destinationOptions = useMemo<DestinationOption[]>(() => {
    if (!options) {
      return [];
    }
    return (options.balances ?? []).map((b) => ({
      key: `${b.token_symbol}|${b.network_symbol || 'no-net'}|${b.wallet_address || b.wallet_id || b.id}`,
      symbol: b.token_symbol,
      networkSymbol: b.network_symbol || '',
      networkName: b.network_name || '',
      iconUrl: b.icon_url ?? null,
      amount: parseFloat(b.available_amount ?? b.amount ?? '0') || 0,
      balance: b,
    }));
  }, [options]);

  const currency = options?.account.symbol || options?.account.currency || 'USDT';

  const sourceLabel = useMemo(() => {
    if (!options) return '';
    const symbol = options.account.symbol || options.account.currency;
    const apyFraction = options.account.apy ? parseFloat(options.account.apy) : null;
    return formatDepositMeta(symbol, options.account.term ?? null, apyFraction) || symbol;
  }, [options]);

  const sourceDateRange = useMemo(() => {
    if (!options) return '';
    const from = formatTermDate(options.account.deposit_date_from);
    const to = formatTermDate(options.account.deposit_date_to);
    return from && to ? `${from} – ${to}` : '';
  }, [options]);

  // Что можно попросить: свободный остаток депозита. Раньше это была
  // пятая часть от него до даты окончания, и то лишь первые 30 дней —
  // экран рисовал этот потолок, а сервер его сторожил. Снятие теперь
  // заявка, которую разбирает персональный менеджер, поэтому от остатка
  // осталась одна арифметика: депозит не отдаст того, чего в нём нет.
  const availableToWithdraw = options?.available_to_withdraw
    ? parseFloat(options.available_to_withdraw) || 0
    : 0;
  const feePercent = options ? parseFloat(options.fee_percent) || 0 : 0;
  const amount = parseAmount(amountStr);
  const feeAmount = amount * feePercent / 100;

  // Disabled-state rules:
  //  - amount > 0 required
  //  - amount ≤ свободный остаток депозита
  //  - destination chosen
  //  - not currently submitting
  const isValidAmount = amount > 0 && amount <= availableToWithdraw;
  const canSubmit = !loading && !!selectedDestination && isValidAmount && !isSubmitting;

  // Amount input turns red as soon as the user asks for more than the
  // deposit holds, even before submit. Empty input stays neutral.
  const amountInputInvalid = amount > 0 && !isValidAmount;

  const handleMaximum = () => {
    // STRING-source (минуя parseFloat) для full precision — см.
    // numberToAmountDisplay docstring про IEEE 754 артефакты на 3.06839508.
    const decimals = Math.min(options?.balance?.token_decimals ?? 2, MAX_AMOUNT_DECIMALS);
    const balanceStr = options?.available_to_withdraw ?? '0';
    setAmountStr(numberToAmountDisplay(balanceStr, decimals));
  };

  const handleSelectDestination = (opt: DestinationOption) => {
    setSelectedDestination(opt.balance);
    setPickerVisible(false);
  };

  const handleConfirm = () => {
    if (!canSubmit || !selectedDestination) {
      return;
    }
    if (!isPINSetup || isPINSkipped) {
      executeConfirm();
    } else {
      setPinModalVisible(true);
    }
  };

  const executeConfirm = async () => {
    setPinModalVisible(false);
    if (!selectedDestination) {
      return;
    }
    setIsSubmitting(true);
    try {
      const attempt = idempotency.attempt({
        accountId: activeAccountId,
        body: { destination_balance_id: selectedDestination.id, amount: toRequestAmount(amountStr) },
      });
      await v2CreateAccountOutflow(attempt.request.accountId, attempt.request.body, attempt.key);
      idempotency.answered();
      // Не вызываем onOutflowSuccess + onBack сразу — показываем
      // success modal "Deposit application / The withdrawal application
      // is under consideration". onClose модалки выполнит callback'и.
      setSuccessModalVisible(true);
    } catch (error) {
      idempotency.failed(error);
      const errorMessage =
        error instanceof Error ? error.message : 'An unexpected error occurred. Please try again.';
      Alert.alert('Withdraw failed', errorMessage, [{ text: 'OK' }]);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseSuccessModal = () => {
    setSuccessModalVisible(false);
    onOutflowSuccess?.();
    onBack();
  };

  const activeDest =
    destinationOptions.find((o) => o.balance.id === selectedDestination?.id) ?? null;
  const destTitle = activeDest
    ? activeDest.networkSymbol
      ? `${activeDest.symbol} - ${activeDest.networkSymbol}`
      : activeDest.symbol
    : '';
  const destSubtitle = activeDest?.networkName || '';
  const destBalanceLabel = activeDest
    ? `${formatBalanceLikeCard(activeDest.amount, activeDest.balance.token_decimals)} ${activeDest.symbol}`
    : '';

  return (
    <View style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor={COLORS.primaryNavy} />
      <SimpleHeader title="Withdraw deposit" onBack={onBack} backgroundVariant="primaryNavy" />

      <View style={styles.keyboardView}>
        <ScrollView
          style={styles.content}
          contentContainerStyle={styles.contentContainer}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          <Pressable onPress={() => setKeypadVisible(false)} accessible={false}>
            {/* From deposit account — tappable picker when the user
                owns 2+ investment deposits. Single-investment users see
                a static tile (chevron hidden). */}
            <Text style={styles.outerCardLabel}>From deposit account</Text>
            {loading ? (
              <View style={[styles.networkSelector, styles.networkSelectorCenter]}>
                <ActivityIndicator size="small" color={COLORS.brandOrange} />
              </View>
            ) : (
              <TouchableOpacity
                style={styles.networkSelector}
                activeOpacity={investmentBalances.length > 1 ? 0.7 : 1}
                disabled={investmentBalances.length < 2}
                onPress={openDepositPicker}
              >
                <TokenIcon
                  symbol={currency}
                  iconUrl={options?.account.icon_url ?? null}
                  style={styles.networkSelectorIcon}/>
                <View style={styles.networkTextContainer}>
                  <Text style={styles.networkName}>{sourceLabel}</Text>
                  {sourceDateRange ? (
                    <Text style={styles.networkSubtitle}>{sourceDateRange}</Text>
                  ) : null}
                </View>
                {investmentBalances.length > 1 ? (
                  <Image
                    source={require('../../../assets/arrow_left_20.png')}
                    style={styles.chevronIcon}
                    resizeMode="contain"
                  />
                ) : null}
              </TouchableOpacity>
            )}

            {/* To current account — picker tile. Always shown so the user
                can see WHERE the funds will land (unlike TopUp where this
                is hidden behind the "From" picker logic). */}
            <Text style={styles.outerCardLabel}>To current account</Text>
            {loading ? (
              <View style={[styles.networkSelector, styles.networkSelectorCenter]}>
                <ActivityIndicator size="small" color={COLORS.brandOrange} />
              </View>
            ) : destinationOptions.length === 0 ? (
              <View style={styles.networkSelector}>
                <Text style={styles.networkName}>
                  No matching current account
                </Text>
                <Text style={styles.networkSubtitle}>
                  Open a {currency} current on the same network first
                </Text>
              </View>
            ) : (
              <TouchableOpacity
                style={styles.networkSelector}
                onPress={() => destinationOptions.length > 1 && setPickerVisible(true)}
                activeOpacity={destinationOptions.length > 1 ? 0.7 : 1}
                disabled={destinationOptions.length <= 1}
              >
                <TokenIcon
                  symbol={activeDest?.symbol ?? currency}
                  iconUrl={activeDest?.iconUrl ?? null}
                  style={styles.networkSelectorIcon}/>
                <View style={styles.networkTextContainer}>
                  <Text style={styles.networkName}>{destTitle}</Text>
                  {destSubtitle ? (
                    <Text style={styles.networkSubtitle}>{destSubtitle}</Text>
                  ) : null}
                </View>
                <Text style={styles.networkBalance}>{destBalanceLabel}</Text>
                {destinationOptions.length > 1 ? (
                  <Image
                    source={require('../../../assets/arrow_left_20.png')}
                    style={styles.chevronIcon}
                    resizeMode="contain"
                  />
                ) : null}
              </TouchableOpacity>
            )}

            {/* Enter amount — same blinking-cursor pattern as TopUp. */}
            <TouchableOpacity
              style={styles.inputContainer}
              activeOpacity={0.7}
              onPress={() => !isSubmitting && setKeypadVisible(true)}
              disabled={isSubmitting}
            >
              <Text style={styles.amountLabel}>Enter amount</Text>
              <Text
                style={[
                  styles.amountInput,
                  amountInputInvalid ? styles.amountInputInvalid : null,
                ]}
              >
                {amountStr || '0'}
              </Text>
              <AmountCaret active={keypadVisible} />
              <Text
                style={[
                  styles.amountSuffix,
                  amountInputInvalid ? styles.amountSuffixInvalid : null,
                ]}
              >
                {currency}
              </Text>
            </TouchableOpacity>

            {/* The fee row only when there is a fee. The available/fee card
                went with the 20 % cap it belonged to, but the fee is a live
                setting — if it is ever raised, the user has to see it before
                confirming, not on the receipt. */}
            {feePercent > 0 && (
              <View style={[styles.infoCard, styles.infoCardFlat]}>
                <View style={styles.infoCardRow}>
                  <Text style={styles.amountLabel}>
                    Fee {feePercent.toFixed(feePercent % 1 === 0 ? 0 : 2)}%:
                  </Text>
                  <Text style={[styles.amountInput, styles.feeAmount, styles.feeAmountRegular]}>
                    {formatBalanceLikeCard(feeAmount, options?.balance?.token_decimals)} {currency}
                  </Text>
                </View>
              </View>
            )}

            {/* A partial withdrawal is a request a personal manager settles
                with the client. The screen used to state the rules it
                enforced — 20 % of the deposit, and only in the first 30 days
                — and refuse anything else; there are no such rules now, on
                either side. */}
            <View style={styles.warningBar}>
              <Image
                source={require('../../../assets/warning.png')}
                style={styles.warningIcon}
                resizeMode="contain"
              />
              <View style={styles.warningTextWrap}>
                <Text style={styles.warningBody}>
                  Withdrawal of the deposit is possible only through a personal manager.
                </Text>
              </View>
            </View>

            {/* Taking out the body is a different operation, and this is the
                way to it. */}
            <View style={styles.noteBar}>
              <Image
                source={require('../../../assets/warning.png')}
                style={styles.noteIcon}
                resizeMode="contain"
              />
              <View style={styles.warningTextWrap}>
                <Text style={styles.noteBody}>
                  To take out the body,{' '}
                  <Text style={styles.noteLink} onPress={onCloseDeposit}>
                    close the deposit
                  </Text>
                </Text>
              </View>
            </View>

            {/* Confirm transfer — navy, disabled when input invalid. */}
            <TouchableOpacity
              style={[
                styles.transferButton,
                (!canSubmit || isSubmitting) && styles.transferButtonDisabled,
              ]}
              onPress={handleConfirm}
              activeOpacity={canSubmit && !isSubmitting ? 0.8 : 1}
              disabled={!canSubmit || isSubmitting}
            >
              <Text
                style={[
                  styles.transferButtonText,
                  (!canSubmit || isSubmitting) && styles.transferButtonTextDisabled,
                ]}
              >
                {isSubmitting ? 'Processing...' : 'Confirm transfer'}
              </Text>
            </TouchableOpacity>

            <View style={{ height: 40 }} />
          </Pressable>
        </ScrollView>
      </View>

      {/* Destination picker bottom-sheet — opens only when 2+ candidates. */}
      <Modal
        visible={pickerVisible}
        transparent
        animationType={WEB_SHEET_MODAL_ANIMATION ?? 'fade'}
        statusBarTranslucent
        onRequestClose={() => setPickerVisible(false)}
      >
        <Pressable style={[styles.modalOverlay, WEB_SHEET_BACKDROP]} onPress={() => setPickerVisible(false)}>
          <Animated.View
            style={[
              styles.modalContent,
              { marginTop: PICKER_TOP_INSET },
              WEB_SHEET,
              { transform: [{ translateY: destSwipe.translateY }] },
            ]}
            {...destSwipe.panHandlers}
          >
            <Pressable onPress={(e) => e.stopPropagation()}>
              <View style={styles.modalHandle} />
              <View style={styles.modalTitleRow}>
                <Text style={styles.modalTitle}>Current accounts</Text>
                <Text style={styles.modalTitleAside}>Available balance</Text>
              </View>
              {destinationOptions.map((opt) => {
                const isSelected = selectedDestination?.id === opt.balance.id;
                const title = opt.networkSymbol
                  ? `${opt.symbol} - ${opt.networkSymbol}`
                  : opt.symbol;
                return (
                  <TouchableOpacity
                    key={opt.key}
                    style={[styles.modalOption, isSelected && styles.modalOptionSelected]}
                    onPress={() => handleSelectDestination(opt)}
                    activeOpacity={0.7}
                  >
                    <TokenIcon
                      symbol={opt.symbol}
                      iconUrl={opt.iconUrl}
                      style={styles.modalOptionIcon}/>
                    <View style={styles.modalOptionTextContainer}>
                      <Text style={styles.modalOptionName}>{title}</Text>
                      {opt.networkName ? (
                        <Text style={styles.modalOptionSubtitle}>{opt.networkName}</Text>
                      ) : null}
                    </View>
                    <Text style={styles.modalOptionAmount}>
                      {formatBalanceLikeCard(opt.amount, opt.balance.token_decimals)} {opt.symbol}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </Pressable>
          </Animated.View>
        </Pressable>
      </Modal>

      {/* Deposit picker bottom-sheet — opens from the "From deposit
          account" tile when the user owns 2+ investment deposits.
          Switching swaps activeAccountId → reloads outflow-options. */}
      <Modal
        visible={depositPickerVisible}
        transparent
        animationType={WEB_SHEET_MODAL_ANIMATION ?? 'fade'}
        statusBarTranslucent
        onRequestClose={() => setDepositPickerVisible(false)}
      >
        <View style={[styles.modalOverlay, WEB_SHEET_BACKDROP]}>
          {/* The backdrop is a sibling of the sheet, not its parent: a
              Pressable above the list takes the touch on Android and the
              native scroll never sees it. */}
          <Pressable style={StyleSheet.absoluteFill} onPress={() => setDepositPickerVisible(false)} />
          <Animated.View
            style={[
              styles.modalContent,
              { marginTop: PICKER_TOP_INSET },
              WEB_SHEET,
              { transform: [{ translateY: depositSwipe.translateY }] },
            ]}
            {...depositSwipe.panHandlers}
          >
            <View style={styles.modalHandle} />
            <Text style={[styles.modalTitle, { marginBottom: 16 }]}>Deposit accounts</Text>
            <ScrollView showsVerticalScrollIndicator={false} {...depositSwipe.listProps}>
              {investmentBalances.map((bal) => {
                const balAccountId = bal.account_id ?? '';
                const isActive = !!balAccountId && balAccountId === activeAccountId;
                const symbol = bal.token_symbol || 'USDT';
                const fromStr = formatTermDate(bal.deposit_date_from);
                const toStr = formatTermDate(bal.deposit_date_to);
                const dateRange = fromStr && toStr ? `${fromStr} – ${toStr}` : fromStr || toStr;
                // Title now uses the unified deposit caption —
                // "USDT · 3 months · 3%" — see formatDepositMeta.
                const apyFraction = bal.apy ? parseFloat(bal.apy) : null;
                const titleParts = formatDepositMeta(symbol, bal.term, apyFraction);
                const subtitleParts = [
                  dateRange,
                  `${formatBalanceLikeCard(bal.amount, bal.token_decimals)} ${symbol}`,
                ].filter(Boolean);
                return (
                  <TouchableOpacity
                    key={bal.id}
                    style={[styles.modalOption, isActive && styles.modalOptionSelected]}
                    activeOpacity={0.7}
                    onPress={() => {
                      setDepositPickerVisible(false);
                      if (balAccountId && balAccountId !== activeAccountId) {
                        // Reset destination + amount — new source has
                        // its own compatible destinations + own 20 %
                        // pre-maturity limit.
                        setSelectedDestination(null);
                        setAmountStr('');
                        setActiveAccountId(balAccountId);
                      }
                    }}
                  >
                    <TokenIcon
                      symbol={symbol}
                      iconUrl={bal.icon_url ?? null}
                      style={styles.modalOptionIcon}/>
                    <View style={styles.modalOptionTextContainer}>
                      <Text style={styles.modalOptionName}>{titleParts}</Text>
                      <Text style={styles.modalOptionSubtitle}>
                        {subtitleParts.join(' · ')}
                      </Text>
                    </View>
                  </TouchableOpacity>
                );
              })}
              </ScrollView>
          </Animated.View>
        </View>
      </Modal>

      <TabBar activeTab="dashboard" onNavigate={onNavigate} />

      <PINConfirmModal
        visible={pinModalVisible}
        title="Confirm withdrawal"
        subtitle={`Withdraw ${formatAmount(amountStr || '0', MAX_AMOUNT_DECIMALS)} ${currency}`}
        onSuccess={executeConfirm}
        onCancel={() => setPinModalVisible(false)}
      />

      {/* Success modal — mirror'ит OpenDepositScreen pattern. Показывается
          после успешного POST /outflow. Amount подписан "−" (списание с
          депозита). Tap outside / X / Ok → handleCloseSuccessModal →
          onOutflowSuccess + onBack. */}
      <Modal
        visible={successModalVisible}
        transparent
        animationType="fade"
        statusBarTranslucent
        onRequestClose={handleCloseSuccessModal}
      >
        <Pressable style={styles.successOverlay} onPress={handleCloseSuccessModal}>
          <Pressable style={styles.successCard} onPress={(e) => e.stopPropagation()}>
            <TouchableOpacity
              style={styles.successCloseBtn}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              onPress={handleCloseSuccessModal}
              activeOpacity={0.6}
            >
              <Text style={styles.successCloseGlyph}>×</Text>
            </TouchableOpacity>

            <Image
              source={require('../../../assets/percentage.png')}
              style={styles.successIcon}
              resizeMode="contain"
            />
            <Text style={styles.successTitle}>Deposit application</Text>
            <Text style={styles.successSubtitle}>
              The withdrawal application is under consideration
            </Text>
            <View style={styles.successAmountRow}>
              <Text style={styles.successAmount}>{`−${formatAmount(amountStr || '0', MAX_AMOUNT_DECIMALS)} `}</Text>
              <Text style={styles.successCurrency}>{currency}</Text>
            </View>

            <TouchableOpacity
              style={styles.successOkButton}
              activeOpacity={0.75}
              onPress={handleCloseSuccessModal}
            >
              <Text style={styles.successOkButtonText}>Ok</Text>
            </TouchableOpacity>
          </Pressable>
        </Pressable>
      </Modal>

      <NumericKeypad
        inline
        visible={keypadVisible}
        value={amountStr}
        // The token's places, eight at most: 0,015 BTC could not be typed at two.
        onChange={(next) => setAmountStr(
          formatAmount(next, Math.min(options?.balance?.token_decimals ?? 2, MAX_AMOUNT_DECIMALS)),
        )}
        onClose={() => setKeypadVisible(false)}
        availableValue={`${formatBalanceLikeCard(options?.available_to_withdraw ?? '0', options?.balance?.token_decimals)} ${currency}`}
        onMax={handleMaximum}
        onClean={() => setAmountStr('')}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: COLORS.primaryNavy,
  },
  keyboardView: {
    flex: 1,
  },
  content: {
    flex: 1,
    backgroundColor: COLORS.pureLight,
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    overflow: 'hidden',
  },
  contentContainer: {
    // 20 dp — uniform with TopUp / Close so the first label sits at
    // the same distance from the header on every deposit screen.
    paddingTop: 20,
    paddingHorizontal: 16,
    paddingBottom: 20,
  },
  outerCardLabel: {
    fontFamily: 'Inter',
    fontWeight: '400',
    fontSize: 16,
    lineHeight: 17,
    color: COLORS.blueSteel,
    marginBottom: 8,
  },
  networkSelector: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 60,
    paddingHorizontal: 12,
    paddingVertical: 10,
    backgroundColor: COLORS.white,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: COLORS.borderMuted,
    marginBottom: 16,
  },
  networkSelectorCenter: {
    justifyContent: 'center',
    minHeight: 64,
  },
  networkSelectorIcon: {
    width: 28,
    height: 28,
    marginRight: 12,
  },
  networkTextContainer: {
    flex: 1,
    justifyContent: 'center',
  },
  networkName: {
    fontFamily: 'Inter',
    fontWeight: '500',
    fontSize: 16,
    lineHeight: 22,
    color: COLORS.textPrimary,
  },
  networkSubtitle: {
    fontFamily: 'Inter',
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 16,
    color: COLORS.blueSteel,
    marginTop: 2,
  },
  networkBalance: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '500',
    lineHeight: 22,
    color: COLORS.textPrimary,
    marginLeft: 8,
    textAlign: 'right',
  },
  chevronIcon: {
    width: 20,
    height: 20,
    marginLeft: 8,
    transform: [{ rotate: '-90deg' }],
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 66,
    paddingHorizontal: 14,
    paddingVertical: 14,
    backgroundColor: COLORS.white,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: COLORS.borderMuted,
    marginBottom: 16,
  },
  amountLabel: {
    flex: 1,
    fontFamily: 'Inter',
    fontWeight: '400',
    fontSize: 16,
    lineHeight: 20,
    color: COLORS.blueSteel,
  },
  amountInput: {
    textAlign: 'right',
    fontFamily: 'Inter',
    fontStyle: 'normal',
    fontSize: 16,
    fontWeight: '500',
    lineHeight: 20,
    color: COLORS.textPrimary,
  },
  amountInputInvalid: {
    color: COLORS_WRONG.amountInvalid,
  },
  amountSuffix: {
    fontFamily: 'Inter',
    fontStyle: 'normal',
    fontWeight: '500',
    fontSize: 16,
    lineHeight: 20,
    color: COLORS.steelNavy,
  },
  amountSuffixInvalid: {
    color: COLORS_WRONG.amountInvalid,
  },
  feeAmount: {
    flex: 1,
    color: COLORS.blueSteel,
  },
  feeAmountRegular: {
    fontWeight: '400',
  },
  infoCard: {
    backgroundColor: COLORS.white,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: COLORS.borderMuted,
    paddingHorizontal: 14,
    paddingVertical: 6,
    marginBottom: 16,
  },
  infoCardFlat: {
    backgroundColor: COLORS.pureLight,
  },
  infoCardRow: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: 38,
  },
  /* Amber warning bar — different palette from the red Close-screen
     warning. Light yellow background, amber border + icon, dark amber
     text. Title is bold; body has a tappable underlined "close the
     deposit" link at the end. */
  warningBar: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 14,
    backgroundColor: '#FFF5D6',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E6A819',
    marginBottom: 12,
  },
  // The second bar carries a way out rather than a caution, so it reads
  // neutral — navy on the screen's own grey — beneath the amber one.
  noteBar: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 14,
    backgroundColor: '#EFF0F3',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: COLORS.primaryNavy,
    marginBottom: 24,
  },
  noteIcon: {
    width: 20,
    height: 20,
    marginRight: 10,
    tintColor: COLORS.primaryNavy,
  },
  noteBody: {
    fontFamily: 'Inter',
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 18,
    color: COLORS.primaryNavy,
  },
  noteLink: {
    textDecorationLine: 'underline',
    fontWeight: '500',
    color: COLORS.primaryNavy,
  },
  warningIcon: {
    width: 20,
    height: 20,
    marginRight: 10,
    marginTop: 2,
    tintColor: '#B0780F',
  },
  warningTextWrap: {
    flex: 1,
  },
  warningBody: {
    fontFamily: 'Inter',
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 18,
    color: '#7A5208',
  },
  transferButton: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 10,
    gap: 12,
    height: 48,
    backgroundColor: COLORS.primaryNavy,
    borderRadius: 8,
    alignSelf: 'stretch',
  },
  transferButtonDisabled: {
    backgroundColor: COLORS_WRONG.disabled,
  },
  transferButtonText: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '500',
    fontStyle: 'normal',
    lineHeight: 20,
    color: COLORS.white,
  },
  transferButtonTextDisabled: {
    color: COLORS.white,
  },
  // Absolutely positioned, not flex: 1 — on Android the Modal's own
  // container does not hand a definite height down, so a flexed overlay
  // sizes itself to its content: the sheet's ScrollView grew as tall as
  // the list, swallowed the sheet's bottom padding, ran off the screen
  // and had nothing left to scroll. Pinning all four edges gives the
  // chain a real height and the list scrolls again.
  modalOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'transparent',
    justifyContent: 'flex-end',
  },
  modalContent: {
    flex: 1,
    backgroundColor: COLORS.pureLight,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    paddingHorizontal: 20,
    paddingTop: 12,
    paddingBottom: 32,
  },
  modalHandle: {
    width: 40,
    height: 4,
    backgroundColor: COLORS.softGrey,
    borderRadius: 2,
    alignSelf: 'center',
    marginBottom: 16,
  },
  modalTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  modalTitle: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '400',
    fontStyle: 'normal',
    lineHeight: 20,
    color: COLORS.primaryNavy,
    // No marginBottom — the parent `modalTitleRow` (flexDirection:
    // row, alignItems: center) owns vertical centering. The bottom
    // gap to the first option is already provided by the row's own
    // marginBottom.
  },
  modalTitleAside: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '400',
    fontStyle: 'normal',
    lineHeight: 20,
    color: COLORS.blueSteel,
  },
  modalOption: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 66,
    paddingHorizontal: 14,
    paddingVertical: 14,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: COLORS.borderMuted,
    marginBottom: 8,
    backgroundColor: COLORS.white,
  },
  modalOptionSelected: {
    borderColor: COLORS.brandOrange,
  },
  modalOptionIcon: {
    width: 28,
    height: 28,
    marginRight: 10,
  },
  modalOptionTextContainer: {
    flex: 1,
    justifyContent: 'center',
  },
  modalOptionName: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '500',
    lineHeight: 22,
    color: COLORS.textPrimary,
  },
  modalOptionSubtitle: {
    fontFamily: 'Inter',
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 16,
    color: COLORS.blueSteel,
    marginTop: 2,
  },
  modalOptionAmount: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '500',
    lineHeight: 22,
    color: COLORS.textPrimary,
    marginLeft: 8,
    textAlign: 'right',
  },
  modalOptionCheck: {
    width: 20,
    height: 20,
    marginLeft: 8,
    tintColor: COLORS.brandOrange,
  },
  // Success modal styles — copy/paste из OpenDepositScreen. Идентичный
  // layout для consistent'ого UX через все три экрана (Open / TopUp /
  // Outflow). При правках синхронизировать со всеми тремя.
  successOverlay: {
    flex: 1,
    backgroundColor: COLORS.overlay,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 24,
  },
  successCard: {
    width: '100%',
    backgroundColor: COLORS.white,
    borderRadius: 16,
    paddingHorizontal: 24,
    paddingTop: 48,
    paddingBottom: 24,
    alignItems: 'center',
  },
  successCloseBtn: {
    position: 'absolute',
    top: 16,
    right: 16,
    width: 32,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
  },
  successCloseGlyph: {
    fontSize: 26,
    lineHeight: 28,
    fontWeight: '400',
    color: COLORS.steelNavy,
  },
  successIcon: {
    width: 64,
    height: 64,
    marginBottom: 28,
  },
  successTitle: {
    fontFamily: 'Inter',
    fontSize: 26,
    fontWeight: '700',
    lineHeight: 32,
    color: COLORS.textPrimary,
    textAlign: 'center',
  },
  successSubtitle: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '400',
    lineHeight: 22,
    color: COLORS.blueSteel,
    textAlign: 'center',
    marginTop: 6,
  },
  successAmountRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    marginTop: 24,
  },
  successAmount: {
    fontFamily: 'Inter',
    fontSize: 36,
    fontWeight: '700',
    lineHeight: 42,
    color: COLORS.mint,
  },
  successCurrency: {
    fontFamily: 'Inter',
    fontSize: 22,
    fontWeight: '500',
    lineHeight: 28,
    color: COLORS.mint,
  },
  successOkButton: {
    alignSelf: 'stretch',
    marginTop: 32,
    height: 48,
    borderRadius: 8,
    backgroundColor: COLORS.primaryNavy,
    alignItems: 'center',
    justifyContent: 'center',
  },
  successOkButtonText: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '500',
    fontStyle: 'normal',
    lineHeight: 20,
    color: COLORS.white,
  },
});

export default OutflowDepositScreen;
